import React from "react";
import {
  FaEdit,
  FaMapMarkerAlt,
  FaSearch,
  FaTrash,
} from "react-icons/fa";

import {
  formatDate,
  isUpcomingEvent,
  resolveMediaUrl,
  truncateText,
} from "../utils/agendaUtils";

export default function AgendaTable({
  rows,
  loading,
  searchTerm,
  statusFilter,
  periodFilter,
  onSearchChange,
  onStatusFilterChange,
  onPeriodFilterChange,
  onEdit,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Tableau des événements
            </h2>

            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {rows.length.toLocaleString("fr-FR")} événement
              {rows.length > 1 ? "s" : ""} affiché
              {rows.length > 1 ? "s" : ""}.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-[minmax(240px,1fr)_170px_170px] xl:min-w-[720px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Rechercher nom, lieu, description..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <select
              value={periodFilter}
              onChange={(event) => onPeriodFilterChange(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">Toutes dates</option>
              <option value="upcoming">À venir</option>
              <option value="past">Passés</option>
            </select>

            <select
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">Tous statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[1120px] w-full text-sm">
            <thead className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Événement
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Description
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Date
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Lieu
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Statut
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-16 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucun événement ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                rows.map((event) => {
                  const upcoming = isUpcomingEvent(event);

                  return (
                    <tr
                      key={event.id}
                      className={[
                        "transition hover:bg-slate-50 dark:hover:bg-slate-900",
                        event.is_active === false
                          ? "bg-slate-50 text-slate-500 dark:bg-slate-900/60"
                          : "",
                      ].join(" ")}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={resolveMediaUrl(event.photoURL)}
                            alt={event.nom_event || "Événement"}
                            className="h-16 w-20 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-white/10"
                            onError={(e) => {
                              e.currentTarget.src =
                                "/images/asso_placeholder.webp";
                            }}
                          />

                          <div className="min-w-0">
                            <p className="font-bold text-slate-900 dark:text-white">
                              {event.nom_event || "Sans titre"}
                            </p>

                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              ID {event.id}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <div className="max-w-[320px] leading-6">
                          {truncateText(event.description_event, 150) || "—"}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <p className="font-bold text-slate-900 dark:text-white">
                            {formatDate(event.date_event)}
                          </p>

                          <span
                            className={[
                              "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
                              upcoming
                                ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                                : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
                            ].join(" ")}
                          >
                            {upcoming ? "À venir" : "Passé"}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <div className="flex max-w-[220px] items-start gap-2">
                          <FaMapMarkerAlt className="mt-1 shrink-0 text-primary" />
                          <span>{event.lieu_event || "—"}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <StatusBadge active={event.is_active !== false} />
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(event)}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                            title="Modifier"
                          >
                            <FaEdit />
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(event.id)}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                            title="Supprimer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={[
        "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      ].join(" ")}
    >
      {active ? "Actif" : "Inactif"}
    </span>
  );
}